<template>
  <div
    class="pos-sidebar"
    :style="{
      background: '#202328',
      color: '#ffffff'
    }"
  >
    <!-- ===================================================== -->
    <!-- ALL PRODUCTS -->
    <!-- ===================================================== -->

    <v-tooltip
      :text="
        currentLanguage === 'fr'
          ? 'Tous les produits'
          : 'All Products'
      "
      location="end"
    >
      <template #activator="{ props: tooltipProps }">
        <button
          v-bind="tooltipProps"
          type="button"
          class="sidebar-item"
          :class="{
            active:
              state.selectedCategoryId === null
          }"
          @click="selectCategory(null)"
        >
          <v-icon
            size="22"
            class="sidebar-icon"
          >
            mdi-view-grid-outline
          </v-icon>

          <span
            v-if="totalProductCount > 0"
            class="sidebar-badge"
          >
            {{ formatCount(totalProductCount) }}
          </span>
        </button>
      </template>
    </v-tooltip>

    <!-- ===================================================== -->
    <!-- CATEGORIES -->
    <!-- ===================================================== -->

    <v-tooltip
      v-for="category in normalizedCategories"
      :key="category.id"
      :text="categoryTooltip(category)"
      location="end"
    >
      <template #activator="{ props: tooltipProps }">
        <button
          v-bind="tooltipProps"
          type="button"
          class="sidebar-item"
          :class="{
            active:
              String(
                state.selectedCategoryId
                ?? ''
              ) ===
              String(
                category.id
                ?? ''
              )
          }"
          @click="selectCategory(category.id)"
        >
          <v-icon
            size="22"
            class="sidebar-icon"
          >
            {{ category.icon }}
          </v-icon>

          <span
            v-if="category.count > 0"
            class="sidebar-badge"
          >
            {{ formatCount(category.count) }}
          </span>
        </button>
      </template>
    </v-tooltip>

    <!-- ===================================================== -->
    <!-- LOADING -->
    <!-- ===================================================== -->

    <div
      v-if="state.loadingCategories"
      class="sidebar-loading"
    >
      <v-progress-circular
        indeterminate
        size="20"
        width="2"
        color="white"
      />
    </div>

    <!-- ===================================================== -->
    <!-- EMPTY -->
    <!-- ===================================================== -->

    <v-tooltip
      v-if="
        !state.loadingCategories &&
        normalizedCategories.length === 0
      "
      :text="
        currentLanguage === 'fr'
          ? 'Aucune catégorie'
          : 'No categories found'
      "
      location="end"
    >
      <template #activator="{ props: tooltipProps }">
        <div
          v-bind="tooltipProps"
          class="sidebar-empty"
        >
          <v-icon
            size="21"
          >
            mdi-shape-outline
          </v-icon>
        </div>
      </template>
    </v-tooltip>
  </div>
</template>

<script setup>
import {
  computed
} from "vue";

import {
  useLangStore
} from "@/store/lang";

import posStore
  from "@/store/pos/posStore";

// ============================================================
// STORES
// ============================================================

const langStore =
  useLangStore();

const state =
  posStore.state;

// ============================================================
// LANGUAGE
// ============================================================

const currentLanguage =
  computed(() =>
    langStore.currentLanguage ||
    "en"
  );

// ============================================================
// TOTAL PRODUCT COUNT
// ============================================================

const totalProductCount =
  computed(() =>
    Array.isArray(
      state.products
    )
      ? state.products.length
      : 0
  );

// ============================================================
// NORMALIZED CATEGORIES
// ============================================================

const normalizedCategories =
  computed(() => {
    const categories =
      Array.isArray(
        state.categories
      )
        ? state.categories
        : [];

    const products =
      Array.isArray(
        state.products
      )
        ? state.products
        : [];

    return categories.map(
      category => {
        const id =
          category.id
          ??
          category.categoryId
          ??
          category.itemCategoryId;

        const name =
          category.name
          ??
          category.categoryName
          ??
          category.description
          ??
          "Category";

        const code =
          category.code
          ??
          "";

        const count =
          products.filter(
            product =>
              String(
                product.categoryId
                ??
                product.itemCategoryId
                ??
                ""
              ) ===
              String(
                id
                ??
                ""
              )
          ).length;

        return {
          id,
          name,
          code,

          icon:
            resolveCategoryIcon(
              code,
              name
            ),

          count
        };
      }
    );
  });

// ============================================================
// SELECT CATEGORY
// ============================================================

function selectCategory(
  categoryId
) {
  posStore.selectCategory(
    categoryId
  );
}

// ============================================================
// TOOLTIP
// ============================================================

function categoryTooltip(
  category
) {
  if (!category) {
    return "";
  }

  if (
    Number(category.count) >
    0
  ) {
    return `${
      category.name
    } (${
      category.count
    })`;
  }

  return category.name;
}

// ============================================================
// COUNT
// ============================================================

function formatCount(
  value
) {
  const numeric =
    Number(
      value
      ??
      0
    );

  if (!Number.isFinite(numeric)) {
    return "";
  }

  if (numeric > 99) {
    return "99+";
  }

  return String(numeric);
}

// ============================================================
// CATEGORY ICON
// ============================================================

function resolveCategoryIcon(
  code,
  name
) {
  const value =
    `${
      code || ""
    } ${
      name || ""
    }`
      .toLowerCase();

  if (
    value.includes(
      "beverage"
    ) ||
    value.includes(
      "drink"
    )
  ) {
    return "mdi-cup-outline";
  }

  if (
    value.includes(
      "food"
    )
  ) {
    return "mdi-food-outline";
  }

  if (
    value.includes(
      "health"
    )
  ) {
    return "mdi-heart-pulse";
  }

  if (
    value.includes(
      "fashion"
    ) ||
    value.includes(
      "cloth"
    )
  ) {
    return "mdi-tshirt-crew-outline";
  }

  if (
    value.includes(
      "electronic"
    )
  ) {
    return "mdi-devices";
  }

  if (
    value.includes(
      "service"
    )
  ) {
    return "mdi-briefcase-outline";
  }

  if (
    value.includes(
      "general"
    )
  ) {
    return "mdi-package-variant-closed";
  }

  if (
    value.includes(
      "test"
    )
  ) {
    return "mdi-flask-outline";
  }

  return "mdi-shape-outline";
}
</script>

<style scoped>
/* ============================================================
   ROOT
============================================================ */

.pos-sidebar {
  width: 56px;
  min-width: 56px;

  height: 100%;
  min-height: 100%;

  display: flex;
  flex-direction: column;
  align-items: stretch;

  border-radius: 0 !important;

  background: #202328 !important;

  color: #f2f4f7 !important;

  overflow-x: visible;
  overflow-y: auto;

  scrollbar-width: none;
}

.pos-sidebar::-webkit-scrollbar {
  display: none;
}

/* ============================================================
   ITEM
============================================================ */

.sidebar-item {
  position: relative;

  width: 56px;
  min-width: 56px;

  height: 54px;
  min-height: 54px;

  padding: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  border: 0;

  border-left:
    3px solid
    transparent;

  background:
    transparent;

  color:
    rgba(
      255,
      255,
      255,
      0.82
    );

  cursor: pointer;

  outline: none;

  transition:
    background-color 0.15s ease,
    color 0.15s ease,
    border-color 0.15s ease;
}

/* ============================================================
   HOVER
============================================================ */

.sidebar-item:hover {
  background:
    rgba(
      255,
      255,
      255,
      0.08
    );

  color: #ffffff;
}

/* ============================================================
   ACTIVE
============================================================ */

.sidebar-item.active {
  border-left-color:
    rgb(
      var(--v-theme-primary)
    );

  background:
    rgba(
      33,
      150,
      243,
      0.13
    );

  color:
    #8fc5ff;
}

/* ============================================================
   ICON
============================================================ */

.sidebar-icon {
  flex-shrink: 0;

  color: inherit;
}

/* ============================================================
   BADGE
============================================================ */

.sidebar-badge {
  position: absolute;

  top: 7px;
  right: 5px;

  min-width: 15px;
  height: 15px;

  padding:
    0
    4px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 8px;

  background:
    rgba(
      255,
      255,
      255,
      0.16
    );

  color: #ffffff;

  font-size: 8px;
  font-weight: 700;

  line-height: 1;

  pointer-events: none;
}

/* ============================================================
   LOADING
============================================================ */

.sidebar-loading {
  width: 56px;
  height: 48px;

  display: flex;
  align-items: center;
  justify-content: center;
}

/* ============================================================
   EMPTY
============================================================ */

.sidebar-empty {
  width: 56px;
  height: 48px;

  display: flex;
  align-items: center;
  justify-content: center;

  color:
    rgba(
      255,
      255,
      255,
      0.45
    );
}

/* ============================================================
   MOBILE
============================================================ */

@media (
  max-width: 959px
) {
  .pos-sidebar {
    width: 100%;
    min-width: 100%;
  }

  .sidebar-item {
    width: 100%;
    min-width: 100%;

    justify-content: center;
  }

  .sidebar-loading,
  .sidebar-empty {
    width: 100%;
  }
}
</style>